<template>
  <v-app-bar
    class="print-hidden app-bar"
    app
    clipped-left
    flat
    :height="64"
    color="#fffaf7"
  >
    <v-app-bar-nav-icon
      v-if="!hideButton"
      class="app-bar__burger"
      :aria-label="burgerLabel"
      @click="$emit('toggle-drawer')"
    />
    <app-logo v-if="!device.isMobile || user.type === 'guest'" />

    <context-switcher
      v-if="user.type !== 'guest'"
      :subtitle="contextSubtitle"
      :page-title="pageTitle"
    />

    <v-spacer />

    <button
      v-if="showSearch"
      type="button"
      class="app-bar__search"
      :aria-label="$t('nav_search_all') + ' (' + shortcutLabel + ')'"
      @click="$emit('open-palette')"
    >
      <v-icon size="19">mdi-magnify</v-icon>
      <span class="app-bar__search-text">{{ $t("nav_search_placeholder") }}</span>
      <kbd class="app-bar__kbd">{{ shortcutLabel }}</kbd>
    </button>

    <v-spacer v-if="showSearch" class="app-bar__sp2" />

    <div class="app-bar__right">
      <v-btn
        v-if="user.type === 'guest'"
        color="primary"
        :to="{ name: 'restorani-guest' }"
        class="mx-2"
      >
        {{ $t("restaurants") }}
      </v-btn>

      <language-chip
        v-if="showLanguage"
        :available-languages="availableLanguages"
        @set-lang="$emit('set-lang', $event)"
      />

      <template v-if="user.type === 'report_guest'">
        <app-report-guest-avatar-menu
          :available-languages="availableLanguages"
          @set-lang="$emit('set-lang', $event)"
          @logout="$emit('logout')"
        />
      </template>
      <app-avatar-menu
        v-else-if="!restaurant || user.type === 'supplier'"
        :show-name="!device.isMobile && !device.isTablet && wide"
      />
      <app-avatar-menu
        v-else-if="user.type !== 'guest'"
        accounts-only
        :show-name="!device.isMobile && !device.isTablet && wide"
      />
    </div>
  </v-app-bar>
</template>

<script>
import { mapState } from "vuex";
import AppLogo from "@/components/layout/AppLogo.vue";
import AppAvatarMenu from "@/components/layout/AppAvatarMenu.vue";
import AppReportGuestAvatarMenu from "@/components/layout/AppReportGuestAvatarMenu.vue";
import ContextSwitcher from "@/components/layout/ContextSwitcher.vue";
import LanguageChip from "@/components/layout/LanguageChip.vue";
import navMenu from "@/mixins/navMenu";

export default {
  name: "AppBar",
  components: {
    AppLogo,
    AppAvatarMenu,
    AppReportGuestAvatarMenu,
    ContextSwitcher,
    LanguageChip,
  },
  mixins: [navMenu],
  props: {
    menu: { type: Array, required: true },
    // Stavke bočnog menija (za naslov stranice na telefonu i prikaz pretrage)
    navMenu: { type: Array, default: () => [] },
    availableLanguages: { type: Array, default: () => [] },
    hideButton: { type: Boolean, default: false },
    drawerOpen: { type: Boolean, default: false },
    rail: { type: Boolean, default: false },
  },
  computed: {
    ...mapState(["company", "restaurant"]),
    user() {
      return this.$store.state.user.user;
    },
    wide() {
      return this.$vuetify.breakpoint.width >= 1280;
    },
    isMac() {
      return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
    },
    shortcutLabel() {
      return this.isMac ? "⌘ K" : "Ctrl K";
    },
    burgerLabel() {
      if (this.device.isDesktop) return this.rail ? this.$t("nav_expand") : this.$t("nav_collapse");
      return this.$t("nav_main_menu");
    },
    // Pretraga svega ima smisla kad ima šta da se traži: više stranica ili više restorana.
    showSearch() {
      if (["guest", "report_guest"].includes(this.user?.type)) return false;
      return this.navMenu.length > 1 || (this.user?.restaurants || []).length > 1;
    },
    showLanguage() {
      if (["guest", "report_guest"].includes(this.user?.type)) return false;
      if (this.device.isMobile) return false; // na telefonu je jezik u meniju sa strane
      return this.availableLanguages.length > 1;
    },
    // Kredit radnika ostaje vidljiv (kao do sada), inače naziv uloge.
    contextSubtitle() {
      const user = this.user;
      if (
        (!this.restaurant || user.type === "supplier") &&
        user.companies &&
        user.companies[0] &&
        user.companies[0].is_credit &&
        user.credit?.length
      ) {
        return `${this.$t("credit_rest")}: ${user.credit[user.credit.length - 1].duguje}`;
      }
      const key = "nav_role_" + user.type;
      return this.$te(key) ? this.$t(key) : "";
    },
    // Naslov trenutne stranice (prikazuje se na telefonu iznad naziva restorana)
    pageTitle() {
      const hit = this.navMenu.find((item) => this.menuItemActive(item));
      return hit ? this.menuLabel(hit) : "";
    },
  },
};
</script>

<style lang="scss">
.app-bar {
  &.v-app-bar {
    border-bottom: 1px solid #f0e2d9 !important;
    padding: 0;
  }
  .v-toolbar__content {
    gap: 8px;
    padding: 0 12px;
  }
  &__burger {
    color: #3a2f28 !important;
  }
  &__search {
    flex: 1 1 460px;
    max-width: 460px;
    min-width: 40px;
    height: 38px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px 0 12px;
    border: 1px solid #f0e2d9;
    border-radius: 12px;
    background: #fff;
    color: #6b5a50;
    text-align: left;
    cursor: pointer;
    &:hover {
      border-color: #e2cdbf;
    }
    &:focus-visible {
      outline: 2px solid #1d4ed8;
      outline-offset: 1px;
    }
  }
  &__search-text {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.9rem;
  }
  &__kbd,
  .v-application &__kbd {
    font: 700 0.68rem "IBM Plex Sans", sans-serif;
    background: #f3ece7 !important;
    border: 1px solid #e6d9cf;
    border-radius: 6px;
    padding: 2px 6px;
    color: #6b5a50 !important;
    box-shadow: none !important;
  }
  &__right {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: none;
  }
}

/* uže: pretraga postaje ikonica */
@media (max-width: 1279px) {
  .app-bar__search {
    flex: none;
    width: 40px;
    padding: 0;
    justify-content: center;
    border-color: transparent;
    background: transparent;
    &:hover {
      background: #f7f1ed;
    }
  }
  .app-bar__search-text,
  .app-bar__kbd,
  .app-bar__sp2 {
    display: none;
  }
}
@media (max-width: 1023px) {
  .app-bar__search {
    width: 44px;
    height: 44px;
  }
}
</style>
